import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  lazy,
  Suspense,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { GrAdd } from "react-icons/gr";
import { FiX } from "react-icons/fi";
import axios from "axios";
import { toast } from "react-toastify";
import Header from "../../header";
import Navvbar from "../../Navvbar";
import "./filesSetting.css";
import "../../../../components/dashboard/cases/newCase/case_id/caseTable/STLViewer.css";

const STLViewer = lazy(() =>
  import("../../../../components/dashboard/cases/newCase/case_id/caseTable/STLViewer")
);

const API_BASE_URL = "https://hms.backend.hiragroupcloud.com";
const PAGE_SIZE = 50;

/* ============================================================
   ✅ FIX (401 Unauthorized): the API expects the JWT from
   localStorage ("accessToken") as a Bearer token. Before, the
   requests only sent cookies (withCredentials), so the server
   rejected them. Every request in this page now sends both.
   ============================================================ */
const getAccessToken = () => {
  try {
    return (
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken") ||
      ""
    );
  } catch {
    return "";
  }
};

const getAuthConfig = (extra = {}) => {
  const token = getAccessToken();
  return {
    withCredentials: true,
    ...extra,
    headers: {
      ...(extra.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
};

// friendly message for auth errors
const getErrorMessage = (err, fallback) => {
  const status = err?.response?.status;
  if (status === 401) return "Your session has expired. Please log in again.";
  if (status === 403) return "You don't have permission to do this.";
  return err?.response?.data?.message || fallback;
};

// ---- Helpers ----
const firstNonEmpty = (...values) => {
  for (const v of values) {
    if (v === undefined || v === null) continue;
    if (typeof v === "string" && v.trim() === "") continue;
    return v;
  }
  return "";
};

const readPractice = (file) =>
  firstNonEmpty(
    file?.practice,
    file?.accountHolderName,
    file?.practiceName,
    file?.account_name
  ) || "Unknown Practice";

const readPatient = (file) =>
  firstNonEmpty(file?.patient, file?.patientName, file?.patient_name) || "-";

const getImageUrl = (path) => {
  if (!path) return "";
  const filename = path.split(/[\\/]/).pop();
  return `${API_BASE_URL}/uploads/${filename}`;
};

const isViewableFile = (file) => {
  const name = (file?.fileName || file?.path || "").toLowerCase();
  return name.endsWith(".stl") || name.endsWith(".ply");
};

const isViewerFile = (file) =>
  file?.source === "viewer" || file?.isViewerFile === true;

/* ============================================================
   STL VIEWER MODAL (unchanged)
   ============================================================ */
const STLFullViewModal = ({ file, fileUrls, onClose }) => {
  const [isModelReady, setIsModelReady] = useState(false);
  const [progress, setProgress] = useState(0);

  const isMulti = Array.isArray(fileUrls) && fileUrls.length > 0;

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  const handleLoad = useCallback(() => setIsModelReady(true), []);
  const handleError = useCallback(() => setIsModelReady(true), []);

  const fileName = isMulti
    ? `${fileUrls.length} files selected`
    : file?.fileName ||
      file?.name ||
      (file?.path ? file.path.split(/[\\/]/).pop() : "") ||
      "File";

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "rgba(0, 0, 0, 0.85)",
        zIndex: 9999,
        padding: "12px",
        boxSizing: "border-box",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(1800px, 96vw)",
          height: "min(900px, 92vh)",
          backgroundColor: "#00a1d6",
          borderRadius: "10px",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            backgroundColor: "#004aad",
            backgroundImage:
              "linear-gradient(115deg, #004aad 0%, #003b88 100%)",
            color: "#fff",
            padding: "14px 22px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "16px",
            flexShrink: 0,
            borderBottom: "1px solid rgba(255,255,255,0.18)",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              minWidth: 0,
              flex: "1 1 auto",
            }}
          >
            <div
              style={{
                fontSize: 17,
                fontWeight: 700,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={fileName}
            >
              {fileName}
            </div>
            <div
              style={{
                fontSize: 11,
                color: "rgba(255,255,255,0.72)",
                letterSpacing: "0.7px",
                textTransform: "uppercase",
                fontWeight: 500,
              }}
            >
              {isMulti
                ? `Multi-Model Viewer · ${fileUrls.length} files`
                : "3D Model Viewer"}
            </div>
          </div>

          <button
            onClick={onClose}
            aria-label="Close viewer"
            title="Close viewer"
            style={{
              backgroundColor: "rgba(255,255,255,0.12)",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: "8px",
              width: 36,
              height: 36,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor =
                "rgba(255,255,255,0.28)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor =
                "rgba(255,255,255,0.12)";
            }}
          >
            <FiX />
          </button>
        </div>

        <div
          style={{
            position: "relative",
            flex: 1,
            minHeight: 0,
            width: "100%",
          }}
        >
          {!isModelReady && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#00a1d6",
                zIndex: 5,
              }}
            >
              <div
                style={{
                  width: "50px",
                  height: "50px",
                  border: "4px solid rgba(255,255,255,0.3)",
                  borderTopColor: "#fff",
                  borderRadius: "50%",
                  animation: "vf-modal-spin 0.8s linear infinite",
                }}
              />
              <p
                style={{
                  color: "#fff",
                  marginTop: "15px",
                  fontSize: "16px",
                }}
              >
                Loading 3D Model{isMulti ? "s" : ""}...
                {progress > 0 && progress < 100 ? ` ${progress}%` : ""}
              </p>
              <style>
                {`@keyframes vf-modal-spin { to { transform: rotate(360deg); } }`}
              </style>
            </div>
          )}

          <div style={{ width: "100%", height: "100%" }}>
            <Suspense fallback={null}>
              <STLViewer
                fileUrl={isMulti ? undefined : getImageUrl(file?.path)}
                fileUrls={isMulti ? fileUrls : undefined}
                size="extra-large"
                shouldLoad={true}
                onProgress={setProgress}
                onLoad={handleLoad}
                onError={handleError}
              />
            </Suspense>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ============================================================
   MAIN COMPONENT
   ============================================================ */
const File = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);

  // ---- Data ----
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  // ---- Pagination (server-driven) ----
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  });

  // ---- Sort ----
  const [sortConfig, setSortConfig] = useState({
    key: "added",
    direction: "desc",
  });

  // ---- STL modal ----
  const [showStlModal, setShowStlModal] = useState(false);
  const [modalFile, setModalFile] = useState(null);
  const [modalFileUrls, setModalFileUrls] = useState(null);

  // ---- Debounced search ----
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to page 1 when search or sort changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, sortConfig]);

  // ---- Fetch (single unified endpoint) ----
  useEffect(() => {
    let cancelled = false;

    const fetchFiles = async () => {
      try {
        setLoading(true);

        const params = {
          page,
          limit: PAGE_SIZE,
          sortBy: sortConfig.key || "added",
          sortDir: sortConfig.direction || "desc",
        };
        if (debouncedSearch) params.search = debouncedSearch;

        // ✅ FIX: send the Bearer token (was cookies only -> 401)
        const res = await axios.get(
          `${API_BASE_URL}/api/viewer-files/all`,
          getAuthConfig({ params })
        );

        if (cancelled) return;

        const data = Array.isArray(res?.data?.data) ? res.data.data : [];
        const pg = res?.data?.pagination || {};

        setFiles(data);
        setPagination({
          page: pg.page ?? page,
          limit: pg.limit ?? PAGE_SIZE,
          total: pg.total ?? data.length,
          totalPages: pg.totalPages ?? 1,
          hasNext: !!pg.hasNext,
          hasPrev: !!pg.hasPrev,
        });

        // If server clamped page (e.g. page=99 → last page), sync
        if (pg.page && pg.page !== page) {
          setPage(pg.page);
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to fetch files:", err);
        toast.error(getErrorMessage(err, "Failed to load files"));
        setFiles([]);
        setPagination((p) => ({ ...p, total: 0, totalPages: 1 }));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchFiles();
    return () => {
      cancelled = true;
    };
  }, [page, debouncedSearch, sortConfig]);

  // ---- Click outside dropdown ----
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearch = (e) => setSearch(e.target.value);

  // ============================================================
  //  View handler (unchanged behavior)
  // ============================================================
  const handleView = (file) => {
    const payload = {
      id: file.id || file._id,
      _id: file._id || file.id,
      caseId: file.caseId || null,
      fileName: file.fileName || file.originalName || "File",
      path: file.path || file.filePath || "",
      added: file.added || "",
      createdAt: file.createdAt || "",
      updated: file.updated || file.added || "",
      updatedAt: file.updatedAt || "",
      practice: file.practice || "Viewer Files",
      patient: file.patient || "-",
      uploadedBy: file.uploadedBy || "",
      description: file.description || "",
      fileType: file.fileType || "",
      fileSize: file.fileSize || 0,
      source: file.source || "case",
      isViewerFile: !!file.isViewerFile,
    };

    try {
      sessionStorage.setItem("viewFileData", JSON.stringify(payload));
    } catch (e) {
      // ignore
    }

    navigate(`/viewFile/${payload.id}`, { state: { file: payload } });
  };

  const handleOpenSingleStl = (file) => {
    setModalFile(file);
    setModalFileUrls(null);
    setShowStlModal(true);
  };

  const handleOpenSelectedInViewer = () => {
    const viewable = files.filter(
      (f) => selected.includes(f.id) && isViewableFile(f)
    );
    if (viewable.length === 0) {
      toast.info("Select at least one STL or PLY file to view");
      return;
    }
    setModalFile(viewable[0]);
    setModalFileUrls(
      viewable.map((f) => ({
        url: getImageUrl(f.path),
        name: f.fileName,
        id: f._id,
      }))
    );
    setShowStlModal(true);
  };

  const handleCloseStlModal = () => {
    setShowStlModal(false);
    setModalFile(null);
    setModalFileUrls(null);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
    setPage(1);
  };

  const allSelected =
    files.length > 0 && files.every((f) => selected.includes(f.id));

  const toggleSelectAll = () => {
    if (allSelected) {
      const ids = new Set(files.map((f) => f.id));
      setSelected((prev) => prev.filter((id) => !ids.has(id)));
    } else {
      const merged = new Set([...selected, ...files.map((f) => f.id)]);
      setSelected([...merged]);
    }
  };

  const toggleSelectOne = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleMenu = (id) => {
    setOpenMenuId((prev) => (prev === id ? null : id));
  };

  const selectedViewableFiles = useMemo(
    () => files.filter((f) => selected.includes(f.id) && isViewableFile(f)),
    [files, selected]
  );

  const totalViewableFiles = useMemo(
    () => files.filter((f) => isViewableFile(f)),
    [files]
  );

  const allViewableSelected =
    totalViewableFiles.length > 0 &&
    selectedViewableFiles.length === totalViewableFiles.length;

  const selectAllViewableFiles = () => {
    const merged = new Set([
      ...selected,
      ...totalViewableFiles.map((f) => f.id),
    ]);
    setSelected([...merged]);
  };

  const deselectAllFiles = () => setSelected([]);

  // ---- DELETE ----
  const handleDelete = async (file) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${file.fileName}"?`
    );
    if (!confirmDelete) return;

    try {
      // ✅ FIX: send the Bearer token here too
      if (isViewerFile(file)) {
        await axios.delete(
          `${API_BASE_URL}/api/viewer-files/${file._id}`,
          getAuthConfig()
        );
      } else {
        await axios.delete(
          `${API_BASE_URL}/api/cases/${file.caseId}/files/${file._id}`,
          getAuthConfig()
        );
      }

      // Optimistic remove from current page
      setFiles((prev) => prev.filter((f) => f.id !== file.id));
      setSelected((prev) => prev.filter((id) => id !== file.id));

      setPagination((p) => {
        const newTotal = Math.max(0, p.total - 1);
        const newTotalPages = Math.max(1, Math.ceil(newTotal / p.limit));
        return { ...p, total: newTotal, totalPages: newTotalPages };
      });

      toast.success("File deleted successfully");

      // If last item on last page was deleted, step back a page
      if (files.length === 1 && page > 1) {
        setPage((p) => p - 1);
      }
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error(getErrorMessage(err, "Failed to delete file"));
    } finally {
      setOpenMenuId(null);
    }
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key)
      return <span className="file-sort-icon">⇅</span>;
    return (
      <span className="file-sort-icon active">
        {sortConfig.direction === "asc" ? "▲" : "▼"}
      </span>
    );
  };

  // ---- Pagination helpers ----
  const total = pagination.total || 0;
  const startEntry =
    total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const endEntry = Math.min(pagination.page * pagination.limit, total);
  const entriesText = `Showing ${startEntry} to ${endEntry} of ${total} entries`;

  const goToPage = (p) => {
    const target = Math.min(Math.max(p, 1), pagination.totalPages || 1);
    if (target !== page) setPage(target);
  };

  const isAddDisabled = loading;

  const getPageNumbers = () => {
    const totalP = pagination.totalPages || 1;
    const current = pagination.page || 1;
    const pages = [];
    const delta = 2;

    const left = Math.max(2, current - delta);
    const right = Math.min(totalP - 1, current + delta);

    pages.push(1);
    if (left > 2) pages.push("...");
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalP - 1) pages.push("...");
    if (totalP > 1) pages.push(totalP);

    return pages;
  };

  return (
    <div className="file-page">
      <div className="file-header-wrap">
        <Header />
        <Navvbar />
      </div>

      <div className="file-container">
        <h1 className="file-title">Files</h1>

        {/* Toolbar */}
        <div className="file-toolbar">
          <div className="file-search-box">
            <label htmlFor="file-search" className="file-search-label">
              Search:
            </label>
            <input
              id="file-search"
              type="text"
              value={search}
              onChange={handleSearch}
              className="file-search-input"
            />
          </div>

          <div className="file-toolbar-right">
            {isAddDisabled ? (
              <button
                type="button"
                className="file-btn-link"
                disabled
                title={loading ? "Loading files..." : "Please wait"}
                style={{
                  opacity: 0.55,
                  cursor: "not-allowed",
                  pointerEvents: "none",
                  border: "none",
                  background: "transparent",
                }}
              >
                <GrAdd /> Add
              </button>
            ) : (
              <Link to="/addNewfile" className="file-btn-link">
                <GrAdd /> Add
              </Link>
            )}

            <Link to="/accountDetails" className="file-btn-link">
              <GrAdd /> Create Case
            </Link>
            <span className="file-entries-info file-entries-desktop">
              {entriesText}
            </span>
          </div>
        </div>

        {/* Selection action bar */}
        {selectedViewableFiles.length > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
              padding: "10px 14px",
              marginBottom: 12,
              borderRadius: 8,
              background: "#f0f6ff",
              border: "1px solid #cfe0fa",
            }}
          >
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "#004aad",
              }}
            >
              {selectedViewableFiles.length} file
              {selectedViewableFiles.length === 1 ? "" : "s"} selected
            </span>

            <button
              type="button"
              onClick={handleOpenSelectedInViewer}
              style={{
                backgroundColor: "#004aad",
                color: "white",
                border: "none",
                padding: "6px 14px",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                boxShadow: "0 2px 6px rgba(0,74,173,0.3)",
              }}
            >
              Open in STL Viewer
            </button>

            {!allViewableSelected && totalViewableFiles.length > 0 && (
              <button
                type="button"
                onClick={selectAllViewableFiles}
                style={{
                  backgroundColor: "#fff",
                  color: "#004aad",
                  border: "1px solid #004aad",
                  padding: "6px 10px",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                Select All
              </button>
            )}

            <button
              type="button"
              onClick={deselectAllFiles}
              style={{
                backgroundColor: "#fff",
                color: "#c93636",
                border: "1px solid #f0c4c4",
                padding: "6px 10px",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              Deselect All
            </button>
          </div>
        )}

        {/* Table */}
        <div className="file-table-wrap">
          <table className="file-table">
            <thead>
              <tr>
                <th className="file-col-check">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th
                  className="file-col-file file-sortable"
                  onClick={() => handleSort("fileName")}
                >
                  <span className="file-th-inner">
                    File {renderSortIcon("fileName")}
                  </span>
                </th>
                <th className="file-col-practice">Practice</th>
                <th className="file-col-patient">Patient</th>
                <th
                  className="file-col-added file-sortable"
                  onClick={() => handleSort("added")}
                >
                  <span className="file-th-inner">
                    Added {renderSortIcon("added")}
                  </span>
                </th>
                <th className="file-col-action"></th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center", padding: 20 }}>
                    Loading files...
                  </td>
                </tr>
              ) : files.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center", padding: 20 }}>
                    No files found
                  </td>
                </tr>
              ) : (
                files.map((file) => {
                  const viewable = isViewableFile(file);
                  return (
                    <tr key={file.id}>
                      <td className="file-col-check">
                        <input
                          type="checkbox"
                          checked={selected.includes(file.id)}
                          onChange={() => toggleSelectOne(file.id)}
                          disabled={!viewable}
                          title={
                            viewable
                              ? "Select for multi-view"
                              : "Only STL / PLY files can be viewed"
                          }
                        />
                      </td>
                      <td className="file-cell-name">{file.fileName}</td>
                      <td>{readPractice(file)}</td>
                      <td>{readPatient(file)}</td>
                      <td>{file.added}</td>
                      <td className="file-col-action">
                        <div
                          className="file-split-btn"
                          ref={openMenuId === file.id ? menuRef : null}
                        >
                          <button
                            type="button"
                            className="file-view-btn"
                            onClick={() => {
                              if (viewable) {
                                handleOpenSingleStl(file);
                              } else {
                                handleView(file);
                              }
                            }}
                            title={
                              viewable
                                ? "Open in STL viewer"
                                : "Open file details"
                            }
                          >
                            View
                          </button>
                          <button
                            type="button"
                            className="file-caret-btn"
                            onClick={() => toggleMenu(file.id)}
                            aria-haspopup="true"
                            aria-expanded={openMenuId === file.id}
                          >
                            <span className="file-caret"></span>
                          </button>

                          {openMenuId === file.id && (
                            <div className="file-dropdown">
                              <button
                                type="button"
                                className="file-dropdown-item"
                                onClick={() => handleView(file)}
                              >
                                View File
                              </button>
                              <button
                                type="button"
                                className="file-dropdown-item"
                                onClick={() => handleDelete(file)}
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* ================= PAGINATION ================= */}
        {!loading && total > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 12,
              padding: "14px 4px",
            }}
          >
            <span style={{ fontSize: 13, color: "#555" }}>
              {entriesText}
            </span>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                onClick={() => goToPage(1)}
                disabled={!pagination.hasPrev}
                className="file-page-btn"
              >
                « First
              </button>
              <button
                type="button"
                onClick={() => goToPage(page - 1)}
                disabled={!pagination.hasPrev}
                className="file-page-btn"
              >
                ‹ Prev
              </button>

              {getPageNumbers().map((p, idx) =>
                p === "..." ? (
                  <span
                    key={`dots-${idx}`}
                    style={{ padding: "0 6px", color: "#888" }}
                  >
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    type="button"
                    onClick={() => goToPage(p)}
                    className="file-page-btn"
                    style={
                      p === pagination.page
                        ? {
                            backgroundColor: "#004aad",
                            color: "#fff",
                            borderColor: "#004aad",
                            fontWeight: 700,
                          }
                        : undefined
                    }
                  >
                    {p}
                  </button>
                )
              )}

              <button
                type="button"
                onClick={() => goToPage(page + 1)}
                disabled={!pagination.hasNext}
                className="file-page-btn"
              >
                Next ›
              </button>
              <button
                type="button"
                onClick={() => goToPage(pagination.totalPages)}
                disabled={!pagination.hasNext}
                className="file-page-btn"
              >
                Last »
              </button>
            </div>
          </div>
        )}

        {/* Mobile footer (kept as-is) */}
        <div className="file-footer">
          <span className="file-footer-info">{entriesText}</span>
          <span className="file-footer-pages">
            <span>First</span>
            <span>Previous</span>
            <span>Next</span>
            <span>Last</span>
          </span>
        </div>
      </div>

      {/* STL Viewer modal */}
      {showStlModal && modalFile && (
        <STLFullViewModal
          file={modalFile}
          fileUrls={modalFileUrls}
          onClose={handleCloseStlModal}
        />
      )}
    </div>
  );
};

export default File;